import { useState, useCallback, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import SubHeaders from '@/components/Headers/SubHeaders'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getContactByObjectId } from '@/modules/Marketing/services'
import useAuthStore from '@/stores/useAuthStore'
import { getChatRoomById, getChatMessages } from '../../services'
import { ChatMessage, ChatRoomStatus } from '../../types/response'
import RoomInfo from './RoomInfo'
import ChatPanel from './ChatPanel'
import SyncToMarketingModal from './SyncToMarketingModal'

const MESSAGE_LIMIT = 50
const ROOM_REFETCH_INTERVAL = 30000 // 30 seconds

const Detail: React.FC = () => {
    const { t } = useTranslation('livechat')
    const { roomId } = useParams<{ roomId: string }>()
    const queryClient = useQueryClient()
    const { employee } = useAuthStore()
    const [roomStatus, setRoomStatus] = useState<ChatRoomStatus | null>(null)
    const [allMessages, setAllMessages] = useState<ChatMessage[]>([])
    const [hasMore, setHasMore] = useState(false)
    const [isLoadingMore, setIsLoadingMore] = useState(false)
    const [syncModalOpen, setSyncModalOpen] = useState(false)
    const [blockModalOpen, setBlockModalOpen] = useState(false)
    const markChatDoneRef = useRef<
        ((roomId: string) => Promise<{ success: boolean }>) | null
    >(null)

    // Use ref to track oldest message ID to avoid stale closure issues
    const oldestMessageIdRef = useRef<string | null>(null)

    const { data: room, renderState: roomRenderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.LIVECHAT_ROOM_DETAIL, roomId],
        queryFn: () => getChatRoomById(roomId!),
        enabled: !!roomId,
        // Auto-refresh room info
        refetchInterval: ROOM_REFETCH_INTERVAL,
        refetchIntervalInBackground: false, // Only refetch when tab is active
        refetchOnWindowFocus: true, // Refetch when user returns to tab
        staleTime: 10000, // Consider data stale after 10 seconds
    })

    const { data: messagesData } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.LIVECHAT_MESSAGES, roomId],
        queryFn: () =>
            getChatMessages({ roomId: roomId!, limit: MESSAGE_LIMIT }),
        enabled: !!roomId,
    })

    // Check if this room has already been synced to marketing
    const { data: existingContact } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.MARKETING, 'sync-check', roomId],
            queryFn: () => getContactByObjectId(roomId!),
            enabled: !!roomId,
        },
        { showLoading: false, showError: false }
    )

    const isAlreadySynced = !!existingContact

    // Initialize messages state when initial data loads
    useEffect(() => {
        if (messagesData) {
            const reversed = [...(messagesData.messages || [])].reverse()
            setAllMessages(reversed)
            setHasMore(messagesData.has_more)
            // Update ref with oldest message ID
            oldestMessageIdRef.current = reversed[0]?.id || null
        }
    }, [messagesData])

    // Load more (older) messages
    const handleLoadMore = useCallback(async () => {
        if (!roomId || isLoadingMore || !hasMore) return

        const oldestId = oldestMessageIdRef.current
        if (!oldestId) return

        setIsLoadingMore(true)

        try {
            const data = await getChatMessages({
                roomId,
                limit: MESSAGE_LIMIT,
                lastMessageId: oldestId,
            })

            // Prepend older messages (reversed because API returns newest first)
            const olderMessages = [...(data.messages || [])].reverse()

            if (olderMessages.length > 0) {
                // Update ref with new oldest message ID
                oldestMessageIdRef.current = olderMessages[0]?.id || oldestId
                setAllMessages((prev) => [...olderMessages, ...prev])
            }

            setHasMore(data.has_more)
        } catch (error) {
            console.error('Failed to load more messages:', error)
        } finally {
            setIsLoadingMore(false)
        }
    }, [roomId, isLoadingMore, hasMore])

    // Handle room status change
    const handleRoomStatusChange = useCallback(
        (status: ChatRoomStatus) => {
            setRoomStatus(status)
            // Invalidate queries to refresh data
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.LIVECHAT_ROOM_DETAIL, roomId],
            })
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.LIVECHAT_ROOMS],
            })
        },
        [queryClient, roomId]
    )

    // Open sync modal — only if not already synced
    const handleSyncToMarketing = useCallback(() => {
        if (!isAlreadySynced) {
            setSyncModalOpen(true)
        }
    }, [isAlreadySynced])

    // Open block modal (block user first, then show sync form)
    const handleOpenBlockModal = useCallback(() => {
        setBlockModalOpen(true)
    }, [])

    const roomStateComponent = roomRenderState()
    if (roomStateComponent) return roomStateComponent

    // Use local status if updated, otherwise use room status
    const currentRoom = room
        ? { ...room, status: roomStatus || room.status }
        : null

    return (
        <div className="h-[calc(100vh-120px)] flex flex-col">
            <SubHeaders
                breadcrumbs={[
                    {
                        path: '/livechat',
                        title: t('Livechat'),
                    },
                    {
                        path: `/livechat/${roomId}`,
                        title: room?.id?.slice(0, 8) || t('Room Detail'),
                    },
                ]}
            />

            <div className="flex-1 flex mt-2 rounded-lg shadow-sm overflow-hidden border border-gray-200">
                {/* Room Info - Left Column */}
                <div className="w-80 flex-shrink-0 border-r border-gray-200 overflow-y-auto bg-white">
                    {currentRoom && (
                        <RoomInfo
                            room={currentRoom}
                            onSyncToMarketing={
                                isAlreadySynced
                                    ? undefined
                                    : handleSyncToMarketing
                            }
                            existingContactId={
                                existingContact?.contact_source_id
                            }
                        />
                    )}
                </div>

                {/* Chat Panel - Right Column */}
                <div className="flex-1 flex flex-col min-w-0 bg-white overflow-hidden">
                    {currentRoom && (
                        <ChatPanel
                            room={currentRoom}
                            initialMessages={allMessages}
                            hasMore={hasMore}
                            isLoadingMore={isLoadingMore}
                            onLoadMore={handleLoadMore}
                            onRoomStatusChange={handleRoomStatusChange}
                            onSyncToMarketing={
                                isAlreadySynced
                                    ? undefined
                                    : handleSyncToMarketing
                            }
                            onOpenBlockModal={handleOpenBlockModal}
                            onMarkChatDoneReady={(fn) => {
                                markChatDoneRef.current = fn
                            }}
                        />
                    )}
                </div>
            </div>

            {/* Sync to Marketing Modal — only when not already synced */}
            {currentRoom && !isAlreadySynced && (
                <SyncToMarketingModal
                    open={syncModalOpen}
                    onClose={() => setSyncModalOpen(false)}
                    room={currentRoom}
                    defaultOverrides={{
                        // Điền sẵn người bấm "Đồng bộ Marketing" làm nhân viên
                        // theo dõi (contact_source_assignee_ids). Không set
                        // status → giữ default UNCLASSIFIED, không vào block mode.
                        contact_source_assignee_ids: employee?.employee_id
                            ? [employee.employee_id]
                            : undefined,
                    }}
                    onAfterSync={async () => {
                        try {
                            await markChatDoneRef.current?.(roomId!)
                        } catch (e) {
                            console.error('Failed to mark chat as done:', e)
                        }
                        handleRoomStatusChange('done')
                        queryClient.invalidateQueries({
                            queryKey: [
                                QUERY_KEYS.MARKETING,
                                'sync-check',
                                roomId,
                            ],
                        })
                    }}
                />
            )}

            {/* Block + Sync to Marketing Modal */}
            {currentRoom && (
                <SyncToMarketingModal
                    open={blockModalOpen}
                    onClose={() => setBlockModalOpen(false)}
                    room={currentRoom}
                    defaultOverrides={{
                        contact_source_status: 'un-qualified-lead',
                        contact_source_assignee_ids: employee?.employee_id
                            ? [employee.employee_id]
                            : undefined,
                    }}
                    onAfterSync={async () => {
                        try {
                            await markChatDoneRef.current?.(roomId!)
                        } catch (e) {
                            console.error('Failed to mark chat as done:', e)
                        }
                        handleRoomStatusChange('done')
                        queryClient.invalidateQueries({
                            queryKey: [
                                QUERY_KEYS.MARKETING,
                                'sync-check',
                                roomId,
                            ],
                        })
                    }}
                />
            )}
        </div>
    )
}

export default Detail
